import { Injectable, Signal, signal } from '@angular/core';
import { BehaviorSubject, catchError, EMPTY, finalize, Subject } from 'rxjs';
import { FlightSearchPayload } from '../models/flight-payload';
import {
  CabinFigure,
  FlightDetailsData,
  PassengerSummary,
} from '../models/cargo-flight-header-model';
import { RegistrationKind } from '../../core/config/action-keypad';
import {
  FlightDocumentPayload,
  FlightLoadControlDocumentationResponse,
} from '../models/flight-document.model';
import { FlightDocumentService } from './flight-document-service';

const PAYLOAD_KEY = 'flightSearchPayload';
const FLIGHT_KEY = 'lastLoadedFlightKey';
const PRODUCT_KEY = 'currentProduct';
const REG_KIND_KEY = 'registrationKind';

type FlightSelectionState = 'none' | 'open' | 'completed';
export function getOverpackNumber(value: any): number | null {
  return value === null || value === undefined || value === ''
    ? null
    : Number(value);
}
export function buildFriendlyError(err: any): string {
  const status = err?.error?.status ?? err?.status;

  if (
    status === 400 &&
    err?.error?.fieldErrors?.length
  ) {
    return err.error.fieldErrors[0].message;
  }

  return (
    err?.error?.message ||
    err?.error?.details?.rootCauseMessage ||
    'A system error occured. Please try again later.'
  );
}

export function normalizeProduct(product: string): string {
  return (product || '').toLowerCase().trim().replace(/\s+/g, '-');
}
@Injectable({
  providedIn: 'root',
})
export class ProductService {
  private readonly deadloadRefreshSubject = new Subject<void>();

deadloadRefresh$ = this.deadloadRefreshSubject.asObservable();
private readonly deadloadWorkflowErrorSubject = new Subject<string>();

deadloadWorkflowError$ = this.deadloadWorkflowErrorSubject.asObservable();

isCargoReleased(): boolean {
  const flightHeader = this.getFlightData();

  return (
    flightHeader?.flightStatus?.loadReleaseStatus?.toUpperCase() === 'FINAL'
  );
}

triggerDeadloadWorkflowError(message: string): void {
  this.deadloadWorkflowErrorSubject.next(message);
}
triggerDeadloadRefresh(): void {
  this.deadloadRefreshSubject.next();
}
  private readonly dgRefreshSource = new Subject<void>();

  dgRefresh$ = this.dgRefreshSource.asObservable();

  triggerDgRefresh(): void {
    this.dgRefreshSource.next();
  }
  private readonly optimaRMActiveSubject = new BehaviorSubject<'Y' | 'N' | ''>('');
  optimaRMActive$ = this.optimaRMActiveSubject.asObservable();
  setOptimaRMActive(val: 'Y' | 'N' | '') {
    this.optimaRMActiveSubject.next(val);
  }
  getOptimaRMActive() {
    return this.optimaRMActiveSubject.value;
  }
  private readonly actionSubject = new Subject<{
    product: string;
    action: string;
    payload?: any;
  }>();
  action$ = this.actionSubject.asObservable();

  dispatchAction(action: { product: string; action: string; payload?: any }) {
    this.actionSubject.next(action);
  }

  private readonly flightDocResultSubject =
    new BehaviorSubject<FlightLoadControlDocumentationResponse | null>(null);
  flightDocResult$ = this.flightDocResultSubject.asObservable();

  private readonly flightDocLoadingSubject = new BehaviorSubject<boolean>(false);
  flightDocLoading$ = this.flightDocLoadingSubject.asObservable();

  private readonly flightDocErrorSubject = new BehaviorSubject<string | null>(null);
  flightDocError$ = this.flightDocErrorSubject.asObservable();

  private readonly subject = new BehaviorSubject<FlightSearchPayload | null>(null);
  payload$ = this.subject.asObservable();

  private readonly _paxSummary = signal<PassengerSummary[]>([]);
  private readonly _headerLoading = signal<boolean>(false);
  private readonly _headerError = signal<string | null>(null);
  private readonly _cabinConfig = signal<CabinFigure[]>([]);
  private readonly _summaryLoading = signal<boolean>(false);
  private readonly _summaryError = signal<string | null>(null);

  private readonly lastLoadedProduct: string | null = null;
  private lastLoadedFlightKey?: string;

  private readonly registrationKindSubject = new BehaviorSubject<RegistrationKind>('Flight');
  registrationKind$ = this.registrationKindSubject.asObservable();

  private readonly productSubject = new BehaviorSubject<string | null>(null);
  product$ = this.productSubject.asObservable();

  private readonly headerTypeSubject = new BehaviorSubject<'welcome' | 'details'>('welcome');
  headerType$ = this.headerTypeSubject.asObservable();

  private readonly flightDataSubject = new BehaviorSubject<FlightDetailsData | null>(null);
  flightData$ = this.flightDataSubject.asObservable();

  private readonly flightSelectionState = new BehaviorSubject<FlightSelectionState>('none');
  flightSelectionState$ = this.flightSelectionState.asObservable();

  private readonly capacityDataSubject = new BehaviorSubject<any[]>([]);
  capacityData$ = this.capacityDataSubject.asObservable();

  private readonly forecastDataSubject = new BehaviorSubject<any[]>([]);
  forecastData$ = this.forecastDataSubject.asObservable();

  private readonly commentsSubject = new BehaviorSubject<any[]>([]);
  comments$ = this.commentsSubject.asObservable();

  private lastFlightKey: string | null = null;

  setLastFlightKey(key: string) {
    this.lastFlightKey = key;
  }

  getLastFlightKey(): string | null {
    return this.lastFlightKey;
  }
  private deadloadRows: any[] = [];
  private deadloadTotalKg = 0;

  constructor(private readonly flightListService: FlightDocumentService) {
    this.hydrateStateFromSession();

    const state = sessionStorage.getItem('flightSelectionState');
    if (state) {
      this.flightSelectionState.next(state as FlightSelectionState);
    }
  }

  private hydrateStateFromSession() {
    const storedPayload = sessionStorage.getItem(PAYLOAD_KEY);
    if (storedPayload) {
      try {
        this.subject.next(JSON.parse(storedPayload));
      } catch {}
    }

    const storedProduct = sessionStorage.getItem(PRODUCT_KEY);
    if (storedProduct) {
      this.productSubject.next(storedProduct);
    }

    const storedKind = sessionStorage.getItem(REG_KIND_KEY);
    if (storedKind) {
      this.registrationKindSubject.next(storedKind as RegistrationKind);
    }

    const storedKey = sessionStorage.getItem(FLIGHT_KEY);
    if (storedKey) {
      this.lastLoadedFlightKey = storedKey;
    }
  }

  private buildFriendlyError(err: any): string {
    return (
      err?.error?.message ||
      err?.error?.details?.rootCauseMessage ||
      'A system error occured. Please try again later.'
    );
  }

  requestFlightDocument(payload: FlightDocumentPayload): void {
    this.flightDocLoadingSubject.next(true);

    this.flightListService
      .getFlightDocument(payload)
      .pipe(
        finalize(() => this.flightDocLoadingSubject.next(false)),
        catchError((err) => {
          this.flightDocErrorSubject.next(this.buildFriendlyError(err));
          this.flightDocResultSubject.next(null);
          return EMPTY;
        })
      )
      .subscribe((response) => {
        this.flightDocErrorSubject.next(null);
        this.flightDocResultSubject.next(response);
      });
  }

  clearFlightDocResult(): void {
    this.flightDocResultSubject.next(null);
    this.flightDocErrorSubject.next(null);
  }

  set(payload: FlightSearchPayload) {
    this.subject.next(payload);
    sessionStorage.setItem(PAYLOAD_KEY, JSON.stringify(payload));
  }

  get(): FlightSearchPayload | null {
    const current = this.subject.value;
    if (current) return current;

    const stored = sessionStorage.getItem(PAYLOAD_KEY);
    if (!stored) return null;

    try {
      const parsed = JSON.parse(stored);
      this.subject.next(parsed);
      return parsed;
    } catch {
      return null;
    }
  }

  setLastLoadedFlightKey(key: string) {
    this.lastLoadedFlightKey = key;
    sessionStorage.setItem(FLIGHT_KEY, key);
  }

  getLastLoadedFlightKey() {
    return this.lastLoadedFlightKey;
  }

  clearLastLoadedFlightKey() {
    this.lastLoadedFlightKey = undefined;
    sessionStorage.removeItem(FLIGHT_KEY);
  }

  setProduct(product: string) {
    this.productSubject.next(product);
    sessionStorage.setItem(PRODUCT_KEY, product);
  }

  getCurrentProduct(): string | null {
    return this.productSubject.getValue();
  }

  setRegistrationKind(kind: RegistrationKind) {
    this.registrationKindSubject.next(kind);
    sessionStorage.setItem(REG_KIND_KEY, kind);
  }

  getRegistrationKind() {
    return this.registrationKindSubject.value;
  }

  get paxSummary(): Signal<PassengerSummary[]> {
    return this._paxSummary;
  }

  setPassengerSummary(value: PassengerSummary[] | null | undefined) {
    this._paxSummary.set(value ?? []);
  }

  setCabinConfig(value: CabinFigure[]) {
    this._cabinConfig.set(value ?? []);
  }

  cabinConfig(): CabinFigure[] {
    return this._cabinConfig();
  }

  get headerLoading(): Signal<boolean> {
    return this._headerLoading;
  }

  setHeaderLoading(value: boolean) {
    this._headerLoading.set(value);
  }

  get headerError(): Signal<string | null> {
    return this._headerError;
  }

  setHeaderError(message: string | null) {
    this._headerError.set(message);
  }

  get summaryLoading(): Signal<boolean> {
    return this._summaryLoading;
  }

  setSummaryLoading(value: boolean) {
    this._summaryLoading.set(value);
  }

  get summaryError(): Signal<string | null> {
    return this._summaryError;
  }

  setSummaryError(message: string | null) {
    this._summaryError.set(message);
  }

  setFlightData(data: FlightDetailsData | null) {
    this.flightDataSubject.next(data);
  }

  getFlightData() {
    return this.flightDataSubject.getValue();
  }

  setHeader(type: 'welcome' | 'details') {
    this.headerTypeSubject.next(type);
  }

  getHeader() {
    return this.headerTypeSubject.getValue();
  }

  setFlightSelectionState(state: FlightSelectionState) {
    this.flightSelectionState.next(state);
    sessionStorage.setItem('flightSelectionState', state);
  }

  getFlightSelectionState() {
    return this.flightSelectionState.getValue();
  }

  setDeadloadRows(rows: any[]) {
    this.deadloadRows = rows ?? [];
  }

  getDeadloadRows() {
    return this.deadloadRows;
  }

  setDeadloadTotalKg(total: number) {
    this.deadloadTotalKg = Number(total) || 0;
  }

  getDeadloadTotalKg() {
    return this.deadloadTotalKg;
  }

  setCapacityData(v: any[]) {
    this.capacityDataSubject.next(v ?? []);
  }

  setForecastData(v: any[]) {
    this.forecastDataSubject.next(v ?? []);
  }

  setComments(v: any[]) {
    this.commentsSubject.next(v ?? []);
  }

  getCapacityData() {
    return this.capacityDataSubject.value;
  }

  getForecastData() {
    return this.forecastDataSubject.value;
  }

  getComments() {
    return this.commentsSubject.value;
  }
  private lastDeadloadKey: string | null = null;

  setLastDeadloadKey(key: string): void {
    this.lastDeadloadKey = key;
  }

  getLastDeadloadKey(): string | null {
    return this.lastDeadloadKey;
  }

  clearLastDeadloadKey(): void {
    this.lastDeadloadKey = null;
  }
}
